import { useCart } from '@corratech/pylot-cart-manager'
import { useEffect, useRef, useState } from 'react'
import { useDataLayerAction } from '../TagContext'
import { CheckoutStepNameEnum, useStoreConfig } from 'config'
import { useRouter } from 'next/router'

type CheckoutStepData = {
    step: CheckoutStepNameEnum
    option?: string
}

const isSameStepData = (
    step1?: CheckoutStepData,
    step2?: CheckoutStepData
): boolean => {
    return (
        !!step1 &&
        !!step2 &&
        step1.step === step2.step &&
        step1.option === step2.option
    )
}

const isCheckoutOptionEvent = (
    step1: CheckoutStepData,
    step2?: CheckoutStepData
): boolean => {
    return !!step1.option && !!step2?.option && step1.option !== step2?.option
}

export const useTrackCheckoutStep = (): ((data: CheckoutStepData) => void) => {
    const lastTrackedStepRef = useRef<CheckoutStepData>()
    const [stepData, setStepData] = useState<CheckoutStepData>()
    const { asPath } = useRouter()

    const { seo } = useStoreConfig()
    const { data } = useCart({
        revalidateOnFocus: false,
        revalidateOnMount: false,
        revalidateOnReconnect: false
    })
    const cart = data?.data?.cart
    const dataLayerAction = useDataLayerAction()
    useEffect(() => {
        if (
            stepData &&
            !isSameStepData(stepData, lastTrackedStepRef.current) &&
            asPath.includes('checkout') &&
            cart
        ) {
            const eventData = {
                step: seo.checkoutSteps[stepData.step],
                option: stepData.option ?? stepData.step
            }

            dataLayerAction({
                type: 'CHECKOUT',
                data: { ...eventData, cart }
            })

            lastTrackedStepRef.current = stepData
        }
    }, [stepData, cart])

    useEffect(() => {
        lastTrackedStepRef.current = undefined
    }, [asPath])

    return setStepData
}
